import React, { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { scanLabel, scanPath } from "../utils/scanCodes";
import { createScanDetector, dialogOpen, isRepeatScan, restoreField, snapshotField } from "../utils/scanWedge";

// Full-screen kiosk / popup routes (the same ones GlobalOverlays leaves bare): a scan there
// must not navigate away from the display.
const NO_SCAN_PREFIXES = ["/laser/", "/station-feed/"];

// A run that restarts within this long after a run of 3+ scanner-speed characters is the same
// scan after a hiccup: keep the field as it was before the first run.
const HICCUP_MS = 1000;
const NOTICE_MS = 4000;

/**
 * Scan a printed JobQ code anywhere in the app (2026-10-21). A USB scanner types the code's
 * link and presses Enter into whatever has focus; this listens to every keydown (window,
 * capture phase, before the page's own handlers), and when utils/scanWedge says the typing
 * was a scan of a JobQ code it swallows the Enter, puts back the text field the scan typed
 * into (if any), and opens /s/<code> (ScanResolvePage), the same place a phone camera and
 * the queue search boxes go.
 *
 * - The same code again is ignored until it has been out of the scanner's view for 3 s
 *   (a hands-free scanner keeps reading a traveler left in front of it).
 * - While a dialog is open (a PIN prompt waits for the task it was opened for) the scan
 *   opens nothing and says so.
 * - A field whose own job is to take a scanned JobQ code (the Beacons page's lot box takes a
 *   pallet's /s/mtn- link to assign a beacon) opts out with the attribute data-own-scans.
 */
export default function ScanWedge() {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const off = NO_SCAN_PREFIXES.some((p) => pathname.startsWith(p));
  const [notice, setNotice] = useState(null);

  // Kept across re-subscriptions (navigate changes identity on every navigation).
  const navigateRef = useRef(navigate);
  navigateRef.current = navigate;
  const pathnameRef = useRef(pathname);
  pathnameRef.current = pathname;
  const stateRef = useRef(null);
  if (stateRef.current === null) stateRef.current = { detector: createScanDetector(), snap: null, last: null };

  useEffect(() => {
    if (!notice) return undefined;
    const id = setTimeout(() => setNotice(null), NOTICE_MS);
    return () => clearTimeout(id);
  }, [notice]);

  useEffect(() => {
    if (off) return undefined;
    const s = stateRef.current;
    s.detector.reset();
    s.snap = null;

    function onKeyDown(e) {
      const target = e.target;
      if (target && typeof target.closest === "function" && target.closest("[data-own-scans]")) {
        s.detector.reset();
        s.snap = null;
        return;
      }
      const answer = s.detector.key(e);
      if (answer.type === "start") {
        // keydown comes before the character lands: this is the field as it was.
        const el = document.activeElement;
        const hiccup = s.snap && s.snap.el === el && answer.prevRun >= 3 && answer.sincePrev <= HICCUP_MS;
        if (!hiccup) s.snap = snapshotField(el);
        return;
      }
      if (answer.type !== "scan") return;
      e.preventDefault();
      e.stopPropagation();
      restoreField(s.snap);
      s.snap = null;
      if (isRepeatScan(s.last, answer.parsed, e.timeStamp)) {
        s.last.at = e.timeStamp;   // still in view: the 3 s start again
        return;
      }
      if (dialogOpen()) {
        setNotice(`Close the open window first, then scan ${scanLabel(answer.parsed)} again.`);
        return;
      }
      s.last = { code: answer.parsed.code, at: e.timeStamp };
      setNotice(null);
      // A scan while the last one is still opening replaces it (Back skips the half-open code).
      navigateRef.current(scanPath(answer.parsed), { replace: pathnameRef.current.toLowerCase().startsWith("/s/") });
    }

    window.addEventListener("keydown", onKeyDown, true);
    return () => window.removeEventListener("keydown", onKeyDown, true);
  }, [off]);

  if (!notice) return null;
  return (
    <div
      className="alert alert-warning shadow py-2 px-3 mb-0"
      role="status"
      data-testid="scan-notice"
      style={{ position: "fixed", top: 12, left: "50%", transform: "translateX(-50%)", zIndex: 2100, maxWidth: "90vw" }}
    >
      <i className="fas fa-qrcode me-2"></i>{notice}
    </div>
  );
}
